import React, { useState, useEffect, useRef } from "react"
import { dataPersistence } from "../utils/DataPersistence.js"
import { getGroupedCSSColorClasses, searchCSSColorClasses, getComputedCSSColor } from "../utils/DynamicCSSColors.js"
import { getSelectionColorInfo } from "../tools.jsx"
import Input from "aldoo-ra/Input"

const ColorPicker = ({ onColorSelect, onClose, selectedColor = null, savedSelection = null }) => {
  const [activeTab, setActiveTab] = useState('palette')
  const [customColor, setCustomColor] = useState('')
  const [colorPalette, setColorPalette] = useState(dataPersistence.getColorPalette())
  const [cssSearchTerm, setCssSearchTerm] = useState('')
  const [groupedCSSClasses, setGroupedCSSClasses] = useState({})
  const [selectionColorInfo, setSelectionColorInfo] = useState(null)
  const colorInputRef = useRef(null)

  // Update palette when data changes
  useEffect(() => {
    const unsubscribe = dataPersistence.addObserver(() => {
      setColorPalette(dataPersistence.getColorPalette())
    })

    return unsubscribe
  }, [])

  // Load CSS classes when component mounts
  useEffect(() => {
    try {
      const grouped = getGroupedCSSColorClasses()
      setGroupedCSSClasses(grouped)
    } catch (error) {
      console.error('Failed to load CSS color classes:', error)
      // Set empty object as fallback
      setGroupedCSSClasses({})
    }
  }, [])

  // Detect current selection color info when component mounts
  useEffect(() => {
    const colorInfo = getSelectionColorInfo(savedSelection)
    setSelectionColorInfo(colorInfo)
    
    // If there's a CSS class applied, switch to CSS classes tab and highlight it
    if (colorInfo && colorInfo.cssClasses.length > 0) {
      setActiveTab('css-classes')
    }
  }, [savedSelection])

  const handleColorClick = (color) => {
    dataPersistence.addRecentColor(color)
    onColorSelect(color)
    onClose()
  }

  const handleCustomColorSubmit = () => {
    const normalizedColor = dataPersistence.normalizeColor(customColor)
    if (normalizedColor) {
      dataPersistence.addCustomColor(normalizedColor)
      dataPersistence.addRecentColor(normalizedColor)
      onColorSelect(normalizedColor)
      setCustomColor('')
      onClose()
    }
  }

  const handleRemoveCustomColor = (color, event) => {
    event.stopPropagation()
    dataPersistence.removeCustomColor(color)
  }

  const handleCSSClassSelect = (className) => {
    onColorSelect(`css-class:${className}`)
    onClose()
  }

  const filteredCSSClasses = cssSearchTerm 
    ? (() => {
        try {
          return searchCSSColorClasses(cssSearchTerm)
        } catch (error) {
          console.error('Failed to search CSS color classes:', error)
          return []
        }
      })()
    : null

  const ColorSwatch = ({ color, onRemove = null, isSelected = false }) => (
    <div className="relative group">
      <div
        className={`w-8 h-8 rounded cursor-pointer border-2 transition-all hover:scale-110 ${
          isSelected ? 'border-admin_primary border-4' : 'border-gray-300'
        }`}
        style={{ backgroundColor: color }}
        onClick={() => handleColorClick(color)}
        title={color}
      />
      {onRemove && (
        <button
          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
          onClick={(e) => onRemove(color, e)}
          title="Remove color"
        >
          ×
        </button>
      )}
    </div>
  )

  return (
    <div 
      className="bg-admin_paper border border-admin_bordered rounded-lg shadow-lg p-4 w-80"
      style={{ userSelect: 'none' }}
    >
      {/* Header */}
      <div className="flex items-center mb-4">
        <h3 className="font-semibold text-admin_text">Select Color</h3>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-admin_bordered mb-4">
        <button
          className={`px-3 py-2 text-sm transition-colors ${
            activeTab === 'palette'
              ? 'border-b-2 border-admin_primary text-admin_primary'
              : 'text-admin_text_secondary hover:text-admin_text'
          }`}
          onClick={() => setActiveTab('palette')}
        >
          Palette
        </button>
        <button
          className={`px-3 py-2 text-sm transition-colors ${
            activeTab === 'custom'
              ? 'border-b-2 border-admin_primary text-admin_primary'
              : 'text-admin_text_secondary hover:text-admin_text'
          }`}
          onClick={() => setActiveTab('custom')}
        >
          Custom
        </button>
        {colorPalette.recent.length > 0 && (
          <button
            className={`px-3 py-2 text-sm transition-colors ${
              activeTab === 'recent'
                ? 'border-b-2 border-admin_primary text-admin_primary'
                : 'text-admin_text_secondary hover:text-admin_text'
            }`}
            onClick={() => setActiveTab('recent')}
          >
            Recent
          </button>
        )}
        <button
          className={`px-3 py-2 text-sm transition-colors ${
            activeTab === 'css-classes'
              ? 'border-b-2 border-admin_primary text-admin_primary'
              : 'text-admin_text_secondary hover:text-admin_text'
          }`}
          onClick={() => setActiveTab('css-classes')}
        >
          CSS Classes
        </button>
      </div>

      {/* Content */}
      {activeTab === 'palette' && (
        <div className="space-y-4">
          {/* Default Colors */}
          <div>
            <h4 className="text-sm font-medium text-admin_text mb-2">Default Colors</h4>
            <div className="grid grid-cols-8 gap-2">
              {colorPalette.default.map((color) => (
                <ColorSwatch
                  key={color}
                  color={color}
                  isSelected={selectedColor === color}
                />
              ))}
            </div>
          </div>

          {/* Custom Colors */}
          {colorPalette.custom.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-admin_text">Custom Colors</h4>
                <button
                  onClick={() => dataPersistence.data.customColors = []}
                  className="text-xs text-admin_text_secondary hover:text-admin_error transition-colors"
                >
                  Clear All
                </button>
              </div>
              <div className="grid grid-cols-8 gap-2">
                {colorPalette.custom.map((color) => (
                  <ColorSwatch
                    key={color}
                    color={color}
                    isSelected={selectedColor === color}
                    onRemove={handleRemoveCustomColor}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'custom' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-admin_text mb-2">
              Custom Color
            </label>
            <div className="flex gap-2">
              <Input
                variant="admin"
                ref={colorInputRef}
                type="color"
                value={customColor || '#000000'}
                onChange={(e) => setCustomColor(e.target.value)}
                className="w-12 h-10 border border-admin_bordered rounded cursor-pointer"
              />
              <Input
                variant="admin"
                type="text"
                value={customColor}
                onChange={(e) => setCustomColor(e.target.value)}
                placeholder="#FF5733 or rgb(255, 87, 51)"
                className="flex-1 px-3 py-2 border border-admin_bordered rounded focus:outline-none focus:border-admin_primary"
                style={{ userSelect: 'text' }}
                onMouseDown={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleCustomColorSubmit}
              disabled={!dataPersistence.isValidColor(customColor)}
              className="flex-1 px-4 py-2 bg-admin_primary text-white rounded hover:bg-admin_primary_dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Add Color
            </button>
            <button
              onClick={() => setCustomColor('')}
              className="px-4 py-2 border border-admin_bordered rounded hover:bg-admin_neutral_1 transition-colors"
            >
              Clear
            </button>
          </div>

          {/* Preview */}
          {dataPersistence.isValidColor(customColor) && (
            <div className="flex items-center gap-3 p-3 bg-admin_neutral_1 rounded">
              <div
                className="w-8 h-8 rounded border"
                style={{ backgroundColor: customColor }}
              />
              <div className="text-sm">
                <div className="font-medium">Preview</div>
                <div className="text-admin_text_secondary">
                  {dataPersistence.normalizeColor(customColor)}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'recent' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-admin_text">Recent Colors</h4>
            <button
              onClick={() => dataPersistence.clearRecentColors()}
              className="text-xs text-admin_text_secondary hover:text-admin_error transition-colors"
            >
              Clear Recent
            </button>
          </div>
          <div className="grid grid-cols-8 gap-2">
            {colorPalette.recent.map((color) => (
              <ColorSwatch
                key={color}
                color={color}
                isSelected={selectedColor === color}
              />
            ))}
          </div>
        </div>
      )}

      {activeTab === 'css-classes' && (
        <div className="space-y-4">
          {/* Search */}
          <div>
            <Input
              variant="admin"
              type="text"
              placeholder="Search CSS classes..."
              value={cssSearchTerm}
              onChange={(e) => setCssSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-admin_bordered rounded focus:outline-none focus:border-admin_primary text-sm"
              style={{ userSelect: 'text' }}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </div>

          {/* Current Selection Info */}
          {selectionColorInfo && (
            <div className="p-3 bg-admin_neutral_1 rounded border text-sm">
              <div className="font-medium text-admin_text mb-2">
                Current Selection: "{selectionColorInfo.selectedText || 'No text selected'}"
              </div>
              {selectionColorInfo.cssClasses.length > 0 && (
                <div className="mb-1">
                  <span className="text-admin_text_secondary">CSS Classes: </span>
                  <span className="font-mono text-xs bg-admin_paper px-1 rounded">
                    {selectionColorInfo.cssClasses.join(', ')}
                  </span>
                </div>
              )}
              {selectionColorInfo.inlineStyle && (
                <div className="mb-1">
                  <span className="text-admin_text_secondary">Inline Style: </span>
                  <span className="font-mono text-xs bg-admin_paper px-1 rounded">
                    {selectionColorInfo.inlineStyle}
                  </span>
                </div>
              )}
              {selectionColorInfo.computedColor && (
                <div className="flex items-center gap-2">
                  <span className="text-admin_text_secondary">Computed: </span>
                  <div 
                    className="w-4 h-4 rounded border border-admin_bordered"
                    style={{ backgroundColor: selectionColorInfo.computedColor }}
                  />
                  <span className="font-mono text-xs">{selectionColorInfo.computedColor}</span>
                </div>
              )}
            </div>
          )}

          {/* CSS Classes List */}
          <div className="max-h-64 overflow-y-auto">
            {filteredCSSClasses ? (
              // Show search results
              <div>
                <h4 className="text-sm font-medium text-admin_text mb-2">
                  Search Results ({filteredCSSClasses.length})
                </h4>
                <div className="space-y-1">
                  {filteredCSSClasses.length > 0 ? (
                    filteredCSSClasses.map((className) => {
                      const computedColor = getComputedCSSColor(className)
                      const isCurrentlyApplied = selectionColorInfo?.cssClasses.includes(className)
                      
                      return (
                        <div
                          key={className}
                          className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors hover:bg-admin_neutral_1 ${
                            isCurrentlyApplied ? 'bg-admin_primary_lt border border-admin_primary' : ''
                          }`}
                          onClick={() => handleCSSClassSelect(className)}
                        >
                          <div 
                            className="w-6 h-6 rounded border border-admin_bordered flex-shrink-0"
                            style={{ backgroundColor: computedColor }}
                            title={computedColor}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-mono text-sm text-admin_text truncate">
                              {className}
                            </div>
                          </div>
                          {isCurrentlyApplied && (
                            <div className="text-xs text-admin_primary font-medium">Applied</div>
                          )}
                        </div>
                      )
                    })
                  ) : (
                    <div className="text-center text-admin_text_secondary py-4">
                      No classes found matching "{cssSearchTerm}"
                    </div>
                  )}
                </div>
              </div>
            ) : Object.keys(groupedCSSClasses).length > 0 ? (
              // Show grouped classes
              Object.entries(groupedCSSClasses).map(([groupName, colorClasses]) => (
                <div key={groupName} className="mb-4">
                  <h4 className="text-sm font-medium text-admin_text mb-2">{groupName}</h4>
                  <div className="space-y-1">
                    {colorClasses.map((colorClass) => {
                      // Handle both old format (strings) and new format (objects)
                      const className = typeof colorClass === 'string' ? colorClass : colorClass.className
                      const computedColor = typeof colorClass === 'string' 
                        ? getComputedCSSColor(className) 
                        : colorClass.computedValue
                      const isCurrentlyApplied = selectionColorInfo?.cssClasses.includes(className)
                      
                      return (
                        <div
                          key={className}
                          className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors hover:bg-admin_neutral_1 ${
                            isCurrentlyApplied ? 'bg-admin_primary_lt border border-admin_primary' : ''
                          }`}
                          onClick={() => handleCSSClassSelect(className)}
                        >
                          <div 
                            className="w-6 h-6 rounded border border-admin_bordered flex-shrink-0"
                            style={{ backgroundColor: computedColor }}
                            title={computedColor}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-mono text-sm text-admin_text truncate">
                              {className}
                            </div>
                          </div>
                          {isCurrentlyApplied && (
                            <div className="text-xs text-admin_primary font-medium">Applied</div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))
            ) : (
              // Fallback when no CSS classes are available
              <div className="text-center text-admin_text_secondary py-8">
                <div className="mb-2">Unable to load CSS color classes</div>
                <div className="text-xs">Check console for details</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="mt-4 pt-4 border-t border-admin_bordered">
        <div className="flex gap-2 text-xs">
          <button
            onClick={() => handleColorClick('transparent')}
            className="px-2 py-1 border border-admin_bordered rounded hover:bg-admin_neutral_1 transition-colors"
          >
            Remove Color
          </button>
          <button
            onClick={() => colorInputRef.current?.click()}
            className="px-2 py-1 border border-admin_bordered rounded hover:bg-admin_neutral_1 transition-colors"
          >
            Color Picker
          </button>
        </div>
      </div>
    </div>
  )
}

export default ColorPicker